import { globals } from '../../../../../../lib/EnvironmentDetection';
import { pastePostProcess, pastePreprocess } from '../utils/tinyUtils';

/* eslint-disable camelcase */
/**
 * Basic HTML elements
 * Used in Comments
 * Empty elements not allowed eg. -p
 * see https://www.tiny.cloud/docs/configure/content-filtering/#valid_elements
 * IMPORTANT: do not use "-" with replcement character "/" like "-strong/-b" as it will lead to falsy behaviour were
 * the whole tag will be removed and not replaced.
 * Proper syntax for that case is: "-strong/b" for example.
 */
const validHtmlElements = [
  '-a[href|rel|target|id|name|aria-describedby|download|type|class|title]',
  '-abbr[class|lang|title]',
  '-blockquote',
  'br',
  '-em/i',
  '-li',
  '-ol',
  '-p',
  '-ul',
  '-span[lang|data-hint-language-key|title]',
  '-strong/b',
];

/**
 * Extended HTML elements
 * Used in most rich text editors
 * IMPORTANT: do not use "-" with replcement character "/" like "-strong/-b" as it will lead to falsy behaviour were
 * the whole tag will be removed and not replaced.
 * Proper syntax for that case is: "-strong/b" for example.
 */
const extendedValidElements = [
  '-caption',
  '-cite[lang]',
  '-code',
  '-col',
  '-colgroup',
  '-del',
  '-div[class]',
  '-h2',
  '-h3',
  '-h4',
  'hr',
  'mark',
  '-pre[class|contenteditable|data-mce-selected]',
  '-span[lang|class|data-forecolor|data-hint-language-key|title]',
  '-sup',
  '-sub',
  '-u',
  '-table[style]',
  '-tbody',
  '#td[scope|colspan|rowspan|style]',
  '-tfoot',
  '-thead',
  '#th[scope|colspan|rowspan|style]',
  '-tr[style]',
  '-use[xlink?href]',
];

/**
 * Tinymce Base Configuration.
 */
const baseConfig = {
  content_css: `/assets/styles/style.editor-content.${globals.bundleHash}.css`,
  base_url: '/assets/tinymce',
  suffix: '.min',
  cache_suffix: globals.bundleHash,
  branding: false,
  paste_data_images: false,
  paste_remove_styles: true,
  remove_script_host: true,
  target_list: false,
  relative_urls: false,
  fix_list_elements: true,
  promotion: false,
  convert_urls: false,
  convert_unsafe_embeds: true,
  sandbox_iframes: true,
  contextmenu: false,
  skin: 'tinymce-5',
  statusbar: true,
  elementpath: true,
  browser_spellcheck: true,
  paste_preprocess: pastePreprocess,
  paste_postprocess: pastePostProcess,
  autosave_retention: '60m',
} as const;

/**
 * The main configuration for the full tinymce.
 * */
export const standard = {
  ...baseConfig,
  min_height: 200,
  // no default styles / attributes
  table_default_styles: {},
  table_default_attributes: {},
  table_appearance_options: false,
  table_advtab: false,
  table_cell_advtab: false,
  table_row_advtab: false,
  table_sizing_mode: 'relative',
  height: '37.5rem', // 600px

  plugins: [
    'autosave',
    'table',
    'linkchooser',
    'anchor',
    'lists',
    'charmap',
    'visualblocks',
    'visualchars',
    'codesample',
    'son_lang',
    'son_hilight',
    'son_abbr',
    'son_fullscreen',
    'emoticons',
    'son_emojis',
    'son_help_plugin',
    'son_mentions',
  ],
  menubar: 'edit insert format table tools',
  menu: {
    edit: {
      title: 'Edit',
      items: 'undo redo | cut copy paste | selectall',
    },
    insert: {
      title: 'Insert',
      items: 'anchor linkchooser unlink | hr charmap | codesample',
    },
    view: {
      title: 'View',
      items: 'visualaid visualblocks visualchars son_fullscreen',
    },
    format: {
      title: 'Format',
      items: 'bold italic underline strikethrough | superscript subscript | removeformat',
    },
    table: {
      title: 'Table',
      items: 'inserttable deletetable | cell row column',
    },
    tools: {
      title: 'Tools',
      items: 'son_lang son_abbr',
    },
  },
  toolbar:
    'blocks | bold italic forecolor son_hilight son_emojis | linkchooser anchor unlink ' +
    '| bullist numlist | removeformat | undo restoredraft redo | son_fullscreen son_help_plugin',
  block_formats:
    'Paragraph=p;Heading 2=h2;Heading 3=h3;Heading 4=h4;Blockquote=blockquote;Preformatted=pre;' +
    'ImportantNotice=custom_important_notice',
  formats: {
    underline: { inline: 'u' },
    strikethrough: { inline: 'del' },
    superscript: { inline: 'sup' },
    subcript: { inline: 'sub' },
    // see readme.dm#forecolor
    forecolor: { inline: 'span', attributes: { 'data-forecolor': '%value' }, classes: 'forecolor' },
    custom_important_notice: {
      inline: 'span',
      classes: 'important-notice',
    },
    removeformat: [
      {
        selector: 'b,strong,em,i,font,u,strike, mark, abbr',
        remove: 'all' as const,
        split: true,
        expand: false,
        block_expand: true,
        deep: true,
      },
      {
        selector: 'span',
        attributes: ['lang', 'style', 'class'], // lang needed here for Sprachauszeichnung Plugin
        remove: 'empty' as const,
        split: true,
        expand: false,
        deep: true,
      },
      {
        selector: 'div',
        attributes: ['class'],
        remove: 'empty' as const,
        split: true,
        expand: false,
        deep: true,
      },
      {
        selector: 'del,sup,sub',
        remove: 'empty' as const,
        split: true,
        expand: false,
        deep: true,
      },
      {
        selector: '*',
        attributes: ['style', 'class'],
        split: false,
        expand: false,
        deep: true,
      },
    ],
  },
  custom_colors: false,
  color_map: [
    /* colors need also be adjusted in wwysiwyg.scss ! */

    '#686868',
    'Grau',
    '#007101',
    'Grün',
    '#ed0000',
    'Rot',
    '#cc3300',
    'Orange',
    '#1155bb',
    'Dunkelblau',
    '#9900CC',
    'Violett',
  ],
  valid_elements: validHtmlElements.join(','),
  extended_valid_elements: extendedValidElements.join(','),
  valid_classes: {
    span:
      'underline infobox-tiny__icon forecolor important-notice ' +
      'invisible download__file-title download-file-icon ' +
      // prism classes set on span. needed for codesample plugin
      'token function punctuation parameter keyword function ' +
      'operator comment prolog doctype cdata namespace property tag boolean number constant symbol deleted ' +
      'selector attr-name string char builtin inserted entity url atrule attr-value class-name regex important ' +
      'variable bold italic',
    div: 'infobox-tiny',
    pre:
      'language-markup language-javascript language-css language-php language-ruby language-python ' +
      'language-java language-c language-csharp language-cpp',
    a: 'download__link download__link--no-margin download__link--no-padding',
    abbr: 'download__abbr',
  },
  valid_styles: {
    th: 'width,height,text-align,vertical-align',
    td: 'width,height,text-align,vertical-align',
    tr: 'width,height,text-align,vertical-align',
    table: 'width,height,text-align,vertical-align',
  },
};

/**
 * small configuration used for comments.
 * */
export const comment = {
  ...baseConfig,
  menubar: false,
  min_height: 100,
  height: '12rem',
  valid_elements: validHtmlElements.join(','),
  plugins: [
    'autosave',
    'linkchooser',
    'lists',
    'son_lang',
    'son_abbr',
    'emoticons',
    'son_emojis',
    'son_help_plugin',
    'son_mentions',
  ],
  toolbar:
    'bold italic blockquote | linkchooser unlink | bullist numlist | son_abbr son_lang | son_emojis son_help_plugin ' +
    '| restoredraft',
};
/* eslint-enable camelcase */
